import { expandState } from '@helpers/geo-location'
import { weekdays, getStandardTime, decodeHtml } from './string-helper'
// eslint-disable-next-line import/no-unresolved
import storeHoursFeed from '../generators/data/store-hours-feed.json'

import { getFromBrowserStorage } from './storage'

export const getStoreSlug = store => {
  const { City, StoreName, StoreType, StoreNumber, State } = store || {}
  const expandedState = expandState(State)
  const name = StoreName ? `-${StoreName}` : ''
  const type = StoreType ? `-${StoreType}` : ''
  const number = StoreNumber ? `-${StoreNumber}` : ''
  return `/stores/${expandedState || State}/${City}${name + type + number}`
    .replace(/\s+/g, '-')
    .toLowerCase()
    .trim()
}

const createCrumb = (label, url) => ({
  category: label,
  id: url,
  url,
  label,
})

export const generateStoreCrumbs = store => [
  createCrumb('Find a Showroom', '/stores'),
  ...(store?.State
    ? [
        createCrumb(
          `Find a Showroom - ${expandState(store.State)}`,
          `/stores/${expandState(store.State)
            .toLowerCase()
            .replace(' ', '-')}`,
        ),
      ]
    : []),
  createCrumb(
    decodeHtml(`${store?.City}, ${store?.State}${store?.StoreName ? ` (${store?.StoreName})` : ''}`),
    getStoreSlug(store),
  ),
]

export const getExtendedStoreType = ({ storeCity, storeState, storeType }) => {
  let storeTypeTitle = `${storeCity}, ${storeState}`
  if (storeType === 'Showroom' || storeType === 'Express') {
    storeTypeTitle = `${storeTypeTitle} Furniture & Mattress Store`
  } else if (storeType === 'Kids') {
    storeTypeTitle = `${storeTypeTitle} Kids Furniture & Mattress Store`
  } else if (storeType === 'Outlet') {
    storeTypeTitle = `${storeTypeTitle} Affordable Furniture Outlet Store`
  } else if (storeType === 'Patio') {
    storeTypeTitle = `${storeTypeTitle} Patio Furniture`
  }
  return storeTypeTitle
}

export const getNearestStore = ({ latitude, longitude }, stores) => {
  let nearestStore
  for (let i = 0; i < stores.length; i++) {
    const store = stores?.[i]?.node || stores?.[i]
    const distance = calculateDistance(
      latitude,
      longitude,
      store?.latitude || store?.Location?.latLng?.lat,
      store?.longitude || store?.Location?.latLng?.lng,
    )

    if (
      (!nearestStore || distance < (nearestStore?.distance || 0)) &&
      store?.State !== 'PR' &&
      store?.StoreType?.toLowerCase() === 'showroom'
    ) {
      nearestStore = { ...store, distance }
    }
  }
  return nearestStore
}

export const getMarkers = (currentLat, currentLng, storeLocs, preferredStore = {}, filteredStoresList) => {
  const markers = []
  const { storeNumber: preferredStoreNumber, relatedStores } = preferredStore

  const stores = storeLocs.filter(
    store =>
      store.StoreTypeId !== 'W' &&
      (filteredStoresList
        ? filteredStoresList?.includes(store?.StoreNumber?.toString()) ||
          !!relatedStores?.filter(relatedStore => +store?.StoreNumber === +relatedStore?.storeNumber).length
        : true),
  )
  for (let i = 0; i < stores.length; i++) {
    stores[i].distance = calculateDistance(
      currentLat,
      currentLng,
      stores[i].latitude || stores[i].Location?.latLng?.lat,
      stores[i].longitude || stores[i].Location?.latLng?.lng,
    )
  }
  stores.sort((a, b) => a.distance - b.distance)
  if (preferredStoreNumber) {
    stores.sort((x, y) => {
      if (x?.StoreNumber === preferredStoreNumber) return -1
      if (y?.StoreNumber === preferredStoreNumber) return 1
      return 0
    })
  }

  stores.forEach((store, index) => {
    const storeDataFromFeed = getStoreInfoFromFeed(store.StoreNumber)
    if (storeDataFromFeed && storeDataFromFeed.store_hours) {
      markers.push({
        title: store.Address1,
        slug: getStoreSlug(store),
        name: getStoreName(store),
        lat: store?.Location?.latLng?.lat,
        lng: store?.Location?.latLng?.lng,
        city: store.City,
        state: store.State,
        storeNumber: store.StoreNumber || store.storeNumber,
        StoreNumber: store.StoreNumber || store.storeNumber,
        zip: store.Zip,
        phone: storeDataFromFeed.phone,
        hours: getStoreHours(storeDataFromFeed.store_hours),
        ratings: store.ratings,
        distance: Math.round(store.distance * 10) / 10,
      })
    }
  })
  return markers
}

export const getStoreName = store => {
  const name = store.Name || store.name
  const city = store.City || store.city
  const storeType = store.StoreType || store.storeType || storesTypes[store.storeTypeId]
  if (name && storeType) {
    return `${city} - ${name} ${storeType}`
  }
  return `${city} ${storeType}`
}

export const getStoreHours = store => {
  let day = []
  const hours = []
  for (let i = 0, n = weekdays.length; i < n; i++) {
    day = weekdays[i]
    hours.push({
      day,
      openTime: store[`${day.toLowerCase()}Open`],
      closeTime: store[`${day.toLowerCase()}Closed`],
    })
  }
  return hours
}

/**
 * Calculates straight-line distance between a pair of lat/lng coordinates (not driving distance)
 * @param {number} lat1 lat for start point
 * @param {number} lon1 lng for start point
 * @param {number} lat2 lat for end point
 * @param {number} lon2 lng for end point
 * @returns {number}
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (lat1 === lat2 && lon1 === lon2) {
    return 0
  }
  const radlat1 = (Math.PI * lat1) / 180
  const radlat2 = (Math.PI * lat2) / 180
  const theta = lon1 - lon2
  const radtheta = (Math.PI * theta) / 180
  let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
  if (dist > 1) {
    dist = 1
  }
  return ((Math.acos(dist) * 180) / Math.PI) * 60 * 1.1515 * 0.8684
}

export const getStore = setOrder => {
  const order = setOrder || getFromBrowserStorage('session', 'order')
  if (order && order.distributionIndex) {
    return getStoreInfoFromFeed(order.distributionIndex)
  }
  return null
}

export const getStoreInfoFromFeed = storeNum => {
  if (storeNum && storeHoursFeed && storeHoursFeed[storeNum]) {
    return storeHoursFeed[storeNum]
  }
  return null
}

export const createStoreDetailPath = ({ StoreName, City, State, StoreType, StoreNumber }) => {
  let nodePath
  if (StoreName) {
    nodePath = `/stores/${expandState(State)
      .toLowerCase()
      .replace(' ', '-')}/${City.toLowerCase().replace(' ', '-')}-${StoreName.toLowerCase().replace(
      ' ',
      '-',
    )}-${StoreType.toLowerCase().replace(' ', '-')}-${StoreNumber}`
  } else {
    nodePath = `/stores/${expandState(State)
      .toLowerCase()
      .replace(' ', '-')}/${City.toLowerCase().replace(' ', '-')}-${StoreType.toLowerCase().replace(
      ' ',
      '-',
    )}-${StoreNumber}`
  }
  return nodePath
}

export const groupStoresByAddress = stores =>
  stores?.reduce?.((storeGroup, store) => {
    const title = store.title || store.address1 || store.Address1
    const name = store.name || getStoreName(store)
    const storeNumber = store.storeNumber || store.StoreNumber
    const slug = store.storeSlug || store.StoreSlug || store.slug
    const storeType = store?.storeType || store?.StoreType
    // if (name.includes('lermont')) debugger
    // Find the storeGroup element in the array
    const index = storeGroup.findIndex(e => e.title === title)
    if (index === -1) {
      //  Store group doesnt exist yet
      storeGroup.push({
        ...store,
        types: [
          {
            name: name
              ?.split(' ')
              ?.slice(-1)
              ?.toString(),
            slug,
            storeNumber,
          },
        ],
        title,
        name,
        StoreNumber: storeNumber,
      })
      return storeGroup
    }
    if (name?.includes('Showroom')) {
      //  Store group does exist and title contains showroom?  If so lets update it and not push a chip
      storeGroup[index].name = name
      storeGroup[index].storeNumber = storeNumber
      storeGroup[index].StoreNumber = storeNumber
      storeGroup[index].slug = slug
      storeGroup[index].StoreType = storeType
      return storeGroup
    }
    storeGroup[index].types.push({
      name: name
        ?.split(' ')
        ?.slice(-1)
        ?.toString(),
      slug,
      storeNumber,
    })
    return storeGroup
  }, []) || []

export const groupStoresTypesByAddress = stores =>
  stores.reduce((storeGroup, store) => {
    const title = store.title || store.address1 || store.Address1
    const name = store.name || getStoreName(store)
    const storeNumber = store.storeNumber || store.StoreNumber
    const storeType = store.storeType || storesTypes[store.storeTypeId]
    const displayName = store.displayName || name
    const storeSlug = store.storeSlug || store.StoreSlug || '#'

    // Find the storeGroup element in the array
    const index = storeGroup.findIndex(e => e.title === title)
    const typeName = name
      ?.split(' ')
      ?.slice(-1)
      ?.toString()
    if (index === -1) {
      //  Store group doesnt exist yet
      storeGroup.push({
        ...store,
        types: [
          {
            name: typeName,
            slug: storeSlug,
            storeNumber,
            phoneNumber: store.phoneNumber,
          },
        ],
        title,
        storeType,
        name: displayName?.split?.(storeType)[0],
        storeSlug,
        StoreNumber: storeNumber,
      })
      return storeGroup
    }
    storeGroup[index].types.push({
      name: name
        ?.split(' ')
        ?.slice(-1)
        ?.toString(),
      slug: storeSlug,
      storeNumber,
      phoneNumber: store.phoneNumber,
    })
    return storeGroup
  }, [])

export function getOpen(store, tomorrow = false, short = false) {
  const today = new Date().getDay()
  const currentDay = tomorrow && today + 1 === 7 ? weekdays[0] : weekdays[today + (tomorrow ? 1 : 0)]
  if (currentDay) {
    const storeOpen = store?.StoreHours?.[`${currentDay?.toLowerCase()}Open`]
    const storeClose = store?.StoreHours?.[`${currentDay?.toLowerCase()}Closed`]
    if (storeOpen && storeClose) {
      if (short)
        return `${
          storeOpen ? `${getStandardTime(storeOpen, false)} - ${getStandardTime(storeClose, false)}` : 'Closed'
        }`
      return `${storeOpen ? `Open from ${getStandardTime(storeOpen)} to ${getStandardTime(storeClose)}` : 'Closed'}`
    }
  }
  return ''
}

export function getHours(store, day) {
  const storeOpen = store.StoreHours?.[`${day.toLowerCase()}Open`]
  const storeClose = store.StoreHours?.[`${day.toLowerCase()}Closed`]
  if (storeOpen && storeClose) {
    return `${getStandardTime(storeOpen)} - ${getStandardTime(storeClose)}`
  }
  return ''
}

/**
 * Converts a string to lowercase and removes spaces
 * @param {string} location name
 * @returns {string} formatted as key id
 */
export const toID = location =>
  location
    .toLowerCase()
    .replaceAll(' ', '-')
    .replaceAll(',', '')

/**
 * Returns a formatted store address
 * @param {string} str store address
 * @returns {string}
 */
export const formatAddress = str => {
  const address = ['ne', 'nw', 'se', 'sw'].includes(str?.substring(str?.length - 2))
    ? str
        .toLowerCase()
        .replace(/\b\w/g, s => s.toUpperCase())
        .replace(/\s\b\w\w$/g, abbrev => abbrev.toUpperCase())
    : str?.toLowerCase().replace(/\b\w/g, s => s.toUpperCase())

  return address
    .replace(' us ', ' US ')
    .replace(' Us ', ' US ')
    .replace(' Ne ', ' NE ')
    .replace(' Nw ', ' NW ')
    .replace(' Se ', ' SE ')
    .replace(' Sw ', ' SW ')
    .replace(/\.$/g, ' ')
}

export const storesTypes = {
  A: 'Showroom',
  C: 'Outlet',
  K: 'Kids',
  P: 'Patio',
}
